// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Container, Group, Menu, Paper, Text, Title, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconLanguage } from '@tabler/icons-react';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage, SUPPORTED_LANGUAGES, type LanguageCode } from '../../i18n';
import classes from './AuthLayout.module.css';

interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  subtitle?: string;
}

export function AuthLayout({ children, title, subtitle }: AuthLayoutProps): JSX.Element {
  const { i18n } = useTranslation();

  const currentLanguage = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (langCode: LanguageCode): void => {
    changeLanguage(langCode);
  };

  return (
    <div className={classes.wrapper}>
      {/* Left side - Full background image */}
      <div className={classes.heroSection}>
        <div className={classes.heroOverlay}>
          <Title order={1} className={classes.heroTitle}>
            CAREPOINT
          </Title>
          <Text className={classes.heroText}>
            The modern digital health platform for the new age of healthcare delivery.
          </Text>
        </div>
      </div>

      {/* Right side - Form section */}
      <div className={classes.formSection}>
        {/* Language Selector - Top Right */}
        <Box className={classes.languageSelector}>
          <Menu shadow="md" width={180} position="bottom-end">
            <Menu.Target>
              <UnstyledButton className={classes.languageButton}>
                <Group gap="xs">
                  <IconLanguage size={16} />
                  <Text size="sm">{currentLanguage.nativeName}</Text>
                  <IconChevronDown size={14} />
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Menu.Item
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  style={{ fontWeight: lang.code === i18n.language ? 600 : 400 }}
                >
                  {lang.nativeName}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        </Box>

        <Container size="xs" className={classes.formContainer}>
          <Box className={classes.logoContainer}>
            <img src="/img/signin-logo.png" alt="Nulou" className={classes.logo} />
          </Box>

          <Paper className={classes.formPaper} radius="lg" p="xl" shadow="sm">
            {title && (
              <Title order={2} ta="center" className={classes.formTitle}>
                {title}
              </Title>
            )}
            {subtitle && (
              <Text c="dimmed" size="sm" ta="center" mb="xl">
                {subtitle}
              </Text>
            )}
            {children}
          </Paper>

          <Text c="dimmed" size="xs" ta="center" className={classes.copyright}>
            Copyright &copy; {new Date().getFullYear()} - Nulou.
          </Text>
        </Container>
      </div>
    </div>
  );
}
